import React from "react";
import { useNavigate } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { fmtINR, pkgImage } from "@/lib/api";
import { useLang, pick } from "@/lib/i18n";
import { Button } from "@/components/ui/button";

export default function PackageCard({ pkg, index = 0 }) {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const inclusions = pkg[`inclusions_${lang}`] || pkg.inclusions_en || [];

  return (
    <div
      className="group relative flex flex-col rounded-2xl overflow-hidden bg-card border border-pink-200 hover:border-gold/40 transition-colors"
      data-testid={`package-card-${pkg.slug}`}
    >
      <div className="relative h-56 overflow-hidden">
        <img
          src={pkgImage(pkg)}
          alt={pick(pkg, lang, "name")}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-card via-card/40 to-transparent" />
        <span className="absolute top-4 left-4 text-[10px] tracking-[0.2em] uppercase bg-black/60 text-gold px-3 py-1.5 rounded-full border border-gold/30">
          {pkg.category}
        </span>
      </div>

      <div className="flex flex-col flex-1 p-6">
        <h3 className="font-display text-xl text-zinc-900 leading-tight">{pick(pkg, lang, "name")}</h3>
        <p className="text-sm text-zinc-600 mt-2 line-clamp-2">{pick(pkg, lang, "description")}</p>

        <ul className="mt-4 space-y-2 flex-1">
          {inclusions.slice(0, 4).map((inc, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-zinc-700">
              <Check size={15} className="text-gold shrink-0 mt-0.5" />
              <span>{inc}</span>
            </li>
          ))}
        </ul>

        <div className="mt-6 pt-5 border-t border-pink-200">
          {pkg.call_for_price ? (
            <div className="mb-3">
              <span className="text-lg font-display gold-text">{t("call_for_price")}</span>
            </div>
          ) : (
            <div className="mb-3">
              <span className="text-xs text-zinc-500">{t("starting_at")}</span>
              <div className="text-2xl font-display gold-text">{fmtINR(pkg.price)}</div>
              {pkg.customization_extra && (
                <span className="text-[11px] text-amber-500/90">
                  {pkg.customization_charge > 0 ? `+ ${fmtINR(pkg.customization_charge)} ${t("customization_extra").replace(/^\+\s*/, "")}` : t("customization_extra")}
                </span>
              )}
            </div>
          )}
          <Button
            onClick={() =>
              pkg.allow_booking && !pkg.call_for_price
                ? navigate(`/book/${pkg.slug}`)
                : navigate("/inquiry")
            }
            data-testid={`book-btn-${pkg.slug}`}
            className="w-full gold-gradient text-white font-semibold rounded-full group-hover:shadow-lg group-hover:shadow-gold/20 transition-shadow"
          >
            {pkg.allow_booking && !pkg.call_for_price ? t("book_now") : t("enquire")}
            <ArrowRight size={16} className="ml-1.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
